// Owns the "Add to cart" action on a platform page that embeds a <linked-seat-stepper>: submits
// BOTH the platform variant (always quantity 1) and whichever seat variant/quantity the stepper
// landed on in ONE request to Shopify's cart, via /cart/add.js's multi-item `items` array --
// deliberately NOT reusing <product-form>/product-form.js, since that's built around a single
// variant per submit and driving it to add a second, unrelated product's line would mean
// fighting its FormData-based design rather than working with it.
//
// Scope note: unlike product-form.js, this does not integrate with the cart drawer/notification
// (PUB_SUB_EVENTS.cartUpdate, section re-rendering, etc.) -- on success it just navigates to the
// cart page (routes.cart_url), matching product-form.js's own fallback path for pages with no
// cart drawer. Revisit if these pages need the slide-out-drawer experience later.
if (!customElements.get('combined-add-to-cart')) {
  customElements.define(
    'combined-add-to-cart',
    class CombinedAddToCart extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.errorTarget = this.querySelector('[data-combined-add-to-cart-error]');
        this.button.addEventListener('click', this.onClick.bind(this));
      }

      // The subscription app's widget sets this field once a plan is chosen (or defaulted) --
      // same assumption assets/seat-total.js already makes; there's no generic Shopify
      // selling-plan picker in this theme, it's entirely delegated to that app. This is the
      // PLATFORM's own plan id -- the seat line must NOT reuse it (see onClick): the seat product
      // has its own, separate selling plan records even for "the same" conceptual offer.
      findPlatformSellingPlanId() {
        return document.querySelector('[name="selling_plan"]')?.value || null;
      }

      setError(message) {
        if (!this.errorTarget) return;
        this.errorTarget.textContent = message || '';
        this.errorTarget.hidden = !message;
      }

      async onClick(event) {
        event.preventDefault();
        if (this.button.disabled) return;
        this.setError(null);

        // Optional required membership field (see main-product's linked_seat_stepper block) --
        // saved on the platform line as a line item property for manual verification.
        const memberField = this.dataset.memberFieldId ? document.getElementById(this.dataset.memberFieldId) : null;
        const memberValue = memberField?.value.trim();
        if (memberField && !memberValue) {
          this.setError(`Please enter your ${this.dataset.memberProperty}.`);
          memberField.focus();
          return;
        }

        this.button.disabled = true;

        const stepper = document.getElementById(this.dataset.stepperId);
        const platformSellingPlanId = this.findPlatformSellingPlanId();
        const platformVariantId = Number(this.dataset.platformVariantId);

        const items = [
          {
            id: platformVariantId,
            quantity: 1,
            ...(platformSellingPlanId ? { selling_plan: Number(platformSellingPlanId) } : {}),
            ...(memberValue ? { properties: { [this.dataset.memberProperty]: memberValue } } : {}),
          },
        ];

        const seatVariantId = stepper?.selectedSeatVariantId;
        const seatQuantity = stepper?.selectedSeatQuantity;
        if (seatVariantId && seatQuantity > 0) {
          // The seat product's OWN selling plan id for the platform's current bucket -- never
          // platformSellingPlanId, which belongs to a different Shopify selling plan record.
          const seatSellingPlanId = stepper?.selectedSeatSellingPlanId;
          items.push({
            id: Number(seatVariantId),
            quantity: seatQuantity,
            ...(seatSellingPlanId ? { selling_plan: Number(seatSellingPlanId) } : {}),
          });
        }

        try {
          await this.clearStaleLines(platformVariantId, stepper);

          const config = fetchConfig('json');
          config.body = JSON.stringify({ items });
          const response = await fetch(`${routes.cart_add_url}`, config);
          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.description || data.message || 'Unable to add to cart.');
          }
          window.location = routes.cart_url;
        } catch (error) {
          console.error('Combined add-to-cart failed', error);
          this.setError(error.message || 'Something went wrong adding this to your cart. Please try again.');
          this.button.disabled = false;
        }
      }

      // This page is meant to represent the customer's WHOLE desired order, not one add among
      // many -- but /cart/add.js is purely additive, so clicking back from checkout, changing
      // the seat count/tier, and clicking through again used to stack a second platform line on
      // top of the first (min 1 turning into 2) and/or leave a stale seat-tier line behind
      // alongside the new one (e.g. an old Tier A line surviving a crossing to Tier B). Wiping
      // any existing cart line for the platform variant or ANY seat tier variant (from the
      // stepper's own tier table, not just the currently active one) right before adding makes
      // every click of this button a clean replacement of this order's lines, regardless of how
      // many times the customer has been through this flow already. Uses each line's unique
      // `key` (not variant id) so this only ever removes exactly the matching lines, even if a
      // stale line carries a different selling plan than the one about to be added.
      async clearStaleLines(platformVariantId, stepper) {
        const relevantVariantIds = new Set([platformVariantId, ...(stepper?.tiers || []).map((tier) => Number(tier.id))]);
        const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
        const staleLines = cart.items.filter((line) => relevantVariantIds.has(line.variant_id));
        if (!staleLines.length) return;

        const updates = {};
        staleLines.forEach((line) => {
          updates[line.key] = 0;
        });
        const config = fetchConfig('json');
        config.body = JSON.stringify({ updates });
        await fetch(`${routes.cart_update_url}`, config);
      }
    },
  );
}
